import React, { useState, useEffect } from "react";
import { FormControl, TextField, Box, Grid2, IconButton } from '@mui/material';
import CheckBoxOutlineBlankIcon from '@mui/icons-material/CheckBoxOutlineBlank';
import CheckBoxIcon from '@mui/icons-material/CheckBox';
import LockIcon from '@mui/icons-material/Lock';

const ConnectionDatabasePatternVariables = /\[[a-zA-Z0-9_]+\]/g;

export default function ConnectionDbForm({ connection, onDataChange, validationError, setValidationError, editMode }) {
    const [data, setData] = useState(JSON.parse(connection.data || '{}'));
    const [variables, setVariables] = useState([]);
    const [loadingEncrypt, setLoadingEncrypt] = useState(false);
    const [encryptedFields, setEncryptedFields] = useState([]);
    const id = connection.id || "new";

    async function encryptValue(value) {
        try {
            setLoadingEncrypt(true);
            const response = await fetch('/api/encryption/encrypt', {
                method: 'POST',
                credentials: 'include',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ value: value }),
            });
            if (response.ok) {
                const result = await response.json();
                return result.encryptedText;
            } else {
                console.error('Encryption failed');
                return value; // Return original value if encryption fails
            }
        } catch (error) {
            console.error('Error during encryption:', error);
            return value; // Return original value on error
        } finally {
            setLoadingEncrypt(false);
        }
    }

    async function decryptValue(value) {
        try {
            setLoadingEncrypt(true);
            const response = await fetch('/api/encryption/decrypt', {
                method: 'POST',
                credentials: 'include',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ value: value }),
            });
            if (response.ok) {
                const result = await response.json();
                return result.decryptedText;
            } else {
                console.error('Decryption failed');
                return value; // Return original value if decryption fails
            }
        } catch (error) {
            console.error('Error during decryption:', error);
            return value; // Return original value on error
        } finally {
            setLoadingEncrypt(false);
        }
    }

    useEffect(() => {
        let newData = JSON.parse(connection.data || '{}');
        setEncryptedFields(newData.decrypt || []);
        const matches = newData.connectionString?.match(ConnectionDatabasePatternVariables);
        if (matches) {
            setVariables(matches.map(variable => variable.replace(/[\[\]]/g, '')));
        } else {
            setVariables([]);
        }
    }, [connection.data]);

    const handleConnectionStringChange = (event) => {
        const newConnectionString = event.target.value;
        setData(prev => ({ ...prev, connectionString: newConnectionString }));
        onDataChange && onDataChange({ ...data, connectionString: newConnectionString });
    }

    const handleVariablesChange = (event) => {
        let variableName = event.target.name.replace('variable-', '');
        variableName = variableName.replace(`-${id}`, '');
        const newValue = event.target.value;
        setData(prev => {
            const newVariables = { ...prev.variables, [variableName]: newValue };
            return { ...prev, variables: newVariables };
        });
        onDataChange && onDataChange({ ...data, variables: { ...data.variables, [variableName]: newValue }, decrypt: encryptedFields });
    }

    const handleEncryptCheckboxChange = async (variable, encrypt) => {
        let val = data.variables?.[variable] || '';
        let newEncryptedFields = [...encryptedFields];
        if (encrypt) {
            const valueToEncrypt = data.variables?.[variable] || '';
            val = await encryptValue(valueToEncrypt);
            newEncryptedFields = [...encryptedFields, variable];
        }
        else {
            val = await decryptValue(data.variables?.[variable] || '');
            newEncryptedFields = encryptedFields.filter(field => field !== variable);
        }
        setEncryptedFields(newEncryptedFields);
        setData(prev => ({
            ...prev,
            variables: { ...prev.variables, [variable]: val }
        }));
        onDataChange && onDataChange({ ...data, variables: { ...data.variables, [variable]: val }, decrypt: newEncryptedFields });
    }

    return (
        <div>
            <h4>Database Connection Details</h4>
            <FormControl fullWidth margin="normal">
                <TextField
                    label="Connection String"
                    id={`connection-string-${id}`}
                    name={`connection-string-${id}`}
                    type="text"
                    value={data.connectionString || ''}
                    onChange={handleConnectionStringChange}
                    InputProps={{
                        readOnly: !editMode
                    }}
                    variant={editMode ? "outlined" : "standard"}
                />
            </FormControl>
            {variables?.length > 0 && <h5>Variables</h5>}
            {variables?.length > 0 && (
                <Grid2 container columns={16} spacing={1}>
                    <Grid2 size={14}>
                    </Grid2>
                    <Grid2 size={1}>
                        <Box display="flex" justifyContent="center" alignItems="center" height="100%">
                            <LockIcon />
                        </Box>
                    </Grid2>
                </Grid2>
            )}
            {variables?.map((variable, index) => (
                <Grid2 container columns={16} spacing={1}>
                    <Grid2 size={14}>
                        <FormControl fullWidth margin="normal" key={index}>
                            <TextField
                                disabled={encryptedFields.includes(variable)}
                                label={`Variable: ${variable}`}
                                id={`variable-${variable}-${id}`}
                                name={`variable-${variable}-${id}`}
                                type="text"
                                value={(data.variables != undefined && variable in data.variables) ? data.variables[variable] : ''}
                                onChange={handleVariablesChange}
                                InputProps={{
                                    readOnly: !editMode
                                }}
                                variant={editMode ? "outlined" : "standard"}
                            />
                        </FormControl>
                    </Grid2>
                    <Grid2 size={1}>
                        <Box display="flex" justifyContent="center" alignItems="center" height="100%">
                            <IconButton variant="outlined" color="primary" size="small" disabled={!editMode}>
                                {encryptedFields.includes(variable) ? (
                                    <CheckBoxIcon onClick={() => handleEncryptCheckboxChange(variable, false)} />
                                ) : (
                                    <CheckBoxOutlineBlankIcon onClick={() => handleEncryptCheckboxChange(variable, true)} />
                                )}
                            </IconButton>
                        </Box>
                    </Grid2>
                </Grid2>
            ))}
        </div>
    );
}

